import React, { useState, useEffect } from 'react';
import { X, Save, Palette, ChevronRight, RotateCcw, Layout } from 'lucide-react';
import { cn } from '../../utils/cn';
import { layoutsApi } from '../../api/client';
import type { PageLayout } from '../../schemas/validation';
import {
  LAYOUT_NONE,
  LAYOUT_SITE_DEFAULT,
  getLayoutDropdownValue,
} from '../../utils/pageLayoutSelection';
import type { BlogCustomColors, BlogSettingsForm } from './types';
import { colorCategories } from './types';

interface BlogSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  siteId: string | undefined;
  settingsForm: BlogSettingsForm;
  setSettingsForm: React.Dispatch<React.SetStateAction<BlogSettingsForm>>;
  customColors: BlogCustomColors;
  showColorPanel: boolean;
  setShowColorPanel: React.Dispatch<React.SetStateAction<boolean>>;
  onSave: () => void;
  onColorChange: (key: keyof BlogCustomColors, value: string) => void;
  onClearColor: (key: keyof BlogCustomColors) => void;
  onResetAllColors: () => void;
  isSaving: boolean;
}

export const BlogSettingsPanel: React.FC<BlogSettingsPanelProps> = ({
  isOpen,
  onClose,
  siteId,
  settingsForm,
  setSettingsForm,
  customColors,
  showColorPanel,
  setShowColorPanel,
  onSave,
  onColorChange,
  onClearColor,
  onResetAllColors,
  isSaving,
}) => {
  const [layouts, setLayouts] = useState<PageLayout[]>([]);
  const [isLoadingLayouts, setIsLoadingLayouts] = useState(false);
  const selectedLayoutValue = getLayoutDropdownValue(
    settingsForm.layoutId,
    settingsForm.showLayout
  );

  useEffect(() => {
    if (!isOpen || !siteId) return;

    const loadLayouts = async () => {
      setIsLoadingLayouts(true);
      try {
        const response = await layoutsApi.list(siteId);
        setLayouts(response.layouts || []);
      } catch (error) {
        console.error('Failed to load layouts:', error);
      } finally {
        setIsLoadingLayouts(false);
      }
    };

    loadLayouts();
  }, [isOpen, siteId]);

  const handlePageLayoutChange = (value: string) => {
    if (value === LAYOUT_NONE) {
      setSettingsForm(prev => ({ ...prev, layoutId: null, showLayout: false }));
    } else if (value === LAYOUT_SITE_DEFAULT) {
      setSettingsForm(prev => ({ ...prev, layoutId: null, showLayout: true }));
    } else {
      setSettingsForm(prev => ({ ...prev, layoutId: value, showLayout: true }));
    }
  };

  const defaultLayout = layouts.find(l => l.isDefault);

  if (!isOpen) return null;

  const customColorCount = Object.keys(customColors).filter(
    k => customColors[k as keyof BlogCustomColors]
  ).length;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      {/* Panel */}
      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        <div className="relative w-screen max-w-md">
          <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Blog Settings</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <div className="flex-1 px-4 py-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Blog Title</label>
                <input
                  type="text"
                  value={settingsForm.title}
                  onChange={e =>
                    setSettingsForm(prev => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="My Blog"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={settingsForm.description}
                  onChange={e =>
                    setSettingsForm(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="A short description of your blog..."
                />
              </div>

              {/* Base Path */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Blog URL Path
                </label>
                <div className="flex items-center">
                  <span className="text-gray-500 text-sm mr-1">yoursite.com</span>
                  <input
                    type="text"
                    value={settingsForm.basePath}
                    onChange={e =>
                      setSettingsForm(prev => ({
                        ...prev,
                        basePath: e.target.value,
                      }))
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="/blog"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  The URL path where your blog will be accessible
                </p>
              </div>

              {/* Page Layout (site chrome) */}
              <div>
                <label
                  htmlFor="blog-page-layout"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Layout size={16} className="text-gray-400" />
                    Page Layout
                  </span>
                </label>
                {isLoadingLayouts ? (
                  <p className="text-sm text-gray-500">Loading layouts...</p>
                ) : (
                  <>
                    <select
                      id="blog-page-layout"
                      aria-label="Page Layout"
                      value={selectedLayoutValue}
                      onChange={e => handlePageLayoutChange(e.target.value)}
                      disabled={isSaving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:opacity-50"
                    >
                      <option value={LAYOUT_SITE_DEFAULT}>
                        Site Default{defaultLayout ? ` (${defaultLayout.name})` : ' (none set)'}
                      </option>
                      <option value={LAYOUT_NONE}>No Layout (Standalone)</option>
                      {layouts.map(layout => (
                        <option key={layout.id} value={layout.id}>
                          {layout.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-2">
                      {selectedLayoutValue === LAYOUT_SITE_DEFAULT
                        ? defaultLayout
                          ? `Wraps blog pages with "${defaultLayout.name}" — includes header & footer.`
                          : 'Uses the site default layout. No default is set — blog will have no header or footer until one is created.'
                        : selectedLayoutValue === LAYOUT_NONE
                          ? 'Blog pages render without site header or footer.'
                          : 'Uses a specific layout for all blog pages.'}
                    </p>
                  </>
                )}
              </div>

              {/* Post list style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post List Style
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(['grid', 'list', 'magazine'] as const).map(layout => (
                    <button
                      key={layout}
                      type="button"
                      onClick={() => setSettingsForm(prev => ({ ...prev, layout }))}
                      className={cn(
                        'px-4 py-2 border rounded-lg text-sm font-medium capitalize transition-colors',
                        settingsForm.layout === layout
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      )}
                    >
                      {layout}
                    </button>
                  ))}
                </div>
              </div>

              {/* Posts Per Page */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Posts Per Page
                </label>
                <select
                  value={settingsForm.postsPerPage}
                  onChange={e =>
                    setSettingsForm(prev => ({
                      ...prev,
                      postsPerPage: parseInt(e.target.value),
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {[5, 10, 15, 20, 25].map(num => (
                    <option key={num} value={num}>
                      {num} posts
                    </option>
                  ))}
                </select>
              </div>

              {/* Display Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Display Options
                </label>
                <div className="space-y-3">
                  {[
                    { key: 'showAuthor', label: 'Show Author' },
                    { key: 'showDate', label: 'Show Date' },
                    { key: 'showReadingTime', label: 'Show Reading Time' },
                    { key: 'showFeaturedImage', label: 'Show Featured Image' },
                    { key: 'showTags', label: 'Show Tags' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settingsForm[key as keyof BlogSettingsForm] as boolean}
                        onChange={e =>
                          setSettingsForm(prev => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Color Customization Toggle */}
              <div className="border-t border-gray-200 pt-6">
                <button
                  type="button"
                  onClick={() => setShowColorPanel(!showColorPanel)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Palette className="h-5 w-5 text-primary-600" />
                    <div className="text-left">
                      <div className="text-sm font-medium text-gray-900">Custom Colors</div>
                      <div className="text-xs text-gray-500">
                        {customColorCount > 0
                          ? `${customColorCount} color(s) customized`
                          : 'Override theme colors'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight
                    className={cn(
                      'h-5 w-5 text-gray-400 transition-transform',
                      showColorPanel && 'rotate-90'
                    )}
                  />
                </button>

                {/* Color Customization Panel */}
                {showColorPanel && (
                  <div className="mt-4 space-y-4 animate-fade-in">
                    {/* Reset Button */}
                    {customColorCount > 0 && (
                      <button
                        type="button"
                        onClick={onResetAllColors}
                        className="flex items-center space-x-2 text-sm text-red-600 hover:text-red-700"
                      >
                        <RotateCcw className="h-4 w-4" />
                        <span>Reset all custom colors</span>
                      </button>
                    )}

                    <p className="text-xs text-gray-500">
                      Override your website theme's default blog colors. Leave blank to use theme
                      defaults.
                    </p>

                    {colorCategories.map(category => (
                      <div key={category.name} className="space-y-2">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {category.name}
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          {category.colors.map(({ key, label }) => (
                            <div key={key} className="flex items-center space-x-2">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-600 mb-1">{label}</label>
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="color"
                                    value={customColors[key as keyof BlogCustomColors] || '#ffffff'}
                                    onChange={e =>
                                      onColorChange(key as keyof BlogCustomColors, e.target.value)
                                    }
                                    className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                                  />
                                  <input
                                    type="text"
                                    value={customColors[key as keyof BlogCustomColors] || ''}
                                    onChange={e =>
                                      onColorChange(key as keyof BlogCustomColors, e.target.value)
                                    }
                                    placeholder="#hex or theme default"
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  />
                                  {customColors[key as keyof BlogCustomColors] && (
                                    <button
                                      type="button"
                                      onClick={() => onClearColor(key as keyof BlogCustomColors)}
                                      className="p-1 text-gray-400 hover:text-red-500"
                                      title="Clear color"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-4 py-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={isSaving}
                className="btn-primary inline-flex items-center space-x-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 animate-spin border-2 border-white border-t-transparent rounded-full" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    <span>Save Settings</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
